import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Tag,
  useColorModeValue,
  Icon,
  LinkBox,
  LinkOverlay,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { PROJECTS, GITHUB_PROFILE } from '@/constants/data';
import { containerVariant, fadeInUp } from '@/utils/animation';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionSimpleGrid = motion(SimpleGrid);

interface ProjectCardProps {
  title: string;
  description: string;
  technologies: string[];
  github?: string;
  liveUrl?: string;
}

const ProjectCard = ({ title, description, technologies, github, liveUrl }: ProjectCardProps) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tagBg = useColorModeValue('green.50', 'green.900');
  const tagColor = useColorModeValue('green.600', 'green.200');

  return (
    <MotionBox
      variants={fadeInUp}
      as={LinkBox}
      bg={bgColor}
      p={6}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      shadow="lg"
      transition="all 0.3s"
      _hover={{
        transform: 'translateY(-5px)',
        shadow: 'xl',
        borderColor: useColorModeValue('green.500', 'green.200'),
      }}
    >
      <VStack align="start" spacing={4}>
        <Heading size="md" mb={2}>
          {title}
        </Heading>

        <Text
          color={useColorModeValue('gray.600', 'gray.400')}
          fontSize="sm"
          noOfLines={3}
        >
          {description}
        </Text>

        <HStack spacing={2} wrap="wrap">
          {technologies.slice(0, 4).map((tech) => (
            <Tag
              key={tech}
              size="sm"
              variant="subtle"
              bg={tagBg}
              color={tagColor}
            >
              {tech}
            </Tag>
          ))}
          {technologies.length > 4 && (
            <Tag
              size="sm"
              variant="subtle"
              bg={tagBg}
              color={tagColor}
            >
              +{technologies.length - 4}
            </Tag>
          )}
        </HStack>

        <HStack spacing={4} mt={2}>
          {github && (
            <LinkOverlay href={github} isExternal>
              <Icon
                as={FaGithub}
                w={5}
                h={5}
                color={useColorModeValue('gray.600', 'gray.400')}
                _hover={{ color: useColorModeValue('green.500', 'green.200') }}
              />
            </LinkOverlay>
          )}
          {liveUrl && (
            <LinkOverlay href={liveUrl} isExternal>
              <Icon
                as={FaExternalLinkAlt}
                w={4}
                h={4}
                color={useColorModeValue('gray.600', 'gray.400')}
                _hover={{ color: useColorModeValue('green.500', 'green.200') }}
              />
            </LinkOverlay>
          )}
        </HStack>
      </VStack>
    </MotionBox>
  );
};

export default function Projects() {
  const { ref, isVisible } = useIntersectionObserver();
  const bgGradient = useColorModeValue(
    'linear(to-b, gray.50, white)',
    'linear(to-b, gray.900, gray.800)'
  );

  return (
    <Box
      as="section"
      id="projects"
      py={20}
      bgGradient={bgGradient}
    >
      <Container maxW="7xl">
        <VStack spacing={12}>
          <Box textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="bold"
              mb={4}
            >
              Featured Projects
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color={useColorModeValue('gray.600', 'gray.400')}
              maxW="2xl"
              mx="auto"
            >
              A collection of blockchain and web development projects showcasing my expertise
            </Text>
          </Box>

          <MotionSimpleGrid
            ref={ref}
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={8}
            w="full"
            initial="initial"
            animate={isVisible ? 'animate' : 'initial'}
            variants={containerVariant}
          >
            {PROJECTS.map((project) => (
              <ProjectCard
                key={project.title}
                title={project.title}
                description={project.description}
                technologies={project.technologies}
                github={project.github}
                liveUrl={project.liveUrl}
              />
            ))}
          </MotionSimpleGrid>

          <Box textAlign="center" mt={12}>
            <Button
              as="a"
              href={GITHUB_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              colorScheme="green"
              leftIcon={<FaGithub />}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
              transition="all 0.3s ease"
            >
              View More Projects on GitHub
            </Button>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
